import React, { useState } from "react";
import { AqiBadge, aqiMeta, pm25Color, WindArrow } from "./aqmHelpers";

const TYPE_ICONS = { Industrial: "🏭", Residential: "🏘", Commercial: "🏢", Traffic: "🚗", "Green Zone": "🌳", "Sensitive Zone": "🏫" };

const AqmStationMapPanel = ({ d }) => {
  const [selected, setSelected] = useState(null);
  const sel = selected ? d.stations.find(s => s.id === selected) : null;
  const selM = sel ? aqiMeta(sel.aqi) : null;

  return (
    <div className="aqm-stationmap-panel">
      <div className="aqm-map-legend">
        {[[50,"Good"],[100,"Moderate"],[150,"Sensitive"],[200,"Unhealthy"],[300,"Very Unhealthy"]].map(([v,l]) => {
          const lm = aqiMeta(v);
          return <span key={v} className="aqm-legend-chip" style={{ background: lm.bg, color: lm.text }}>● {l}</span>;
        })}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af" }}>Click any station pin for details</span>
      </div>

      <div className="aqm-map-body">
        {/* SVG City Map */}
        <div className="aqm-map-svg-wrap">
          <svg viewBox="0 0 100 85" className="aqm-map-svg" preserveAspectRatio="xMidYMid meet">
            {/* Background */}
            <rect width="100" height="85" fill="#f0fdf4" rx="2" />

            {/* City grid lines */}
            {[20,40,60,80].map(x => <line key={`v${x}`} x1={x} y1="0" x2={x} y2="85" stroke="#d1fae5" strokeWidth="0.3" />)}
            {[20,40,60].map(y => <line key={`h${y}`} x1="0" y1={y} x2="100" y2={y} stroke="#d1fae5" strokeWidth="0.3" />)}

            {/* Title */}
            <text x="50" y="6" textAnchor="middle" fontSize="2.2" fontWeight="700" fill="#6b7280">PUNE CITY — AIR QUALITY MONITORING NETWORK</text>

            {/* Wind direction arrow */}
            <g transform="translate(88, 10)">
              <circle cx="0" cy="0" r="5" fill="white" stroke="#bae6fd" strokeWidth="0.5" />
              {/* N label */}
              <text x="0" y="-3" textAnchor="middle" fontSize="1.5" fill="#64748b" fontWeight="600">N</text>
              {/* Wind arrow pointing SW (225°) */}
              <line x1="0" y1="0" x2="-2.5" y2="2.5" stroke="#0369a1" strokeWidth="0.8" strokeLinecap="round" />
              <circle cx="-2.5" cy="2.5" r="0.6" fill="#0369a1" />
              <text x="0" y="6" textAnchor="middle" fontSize="1.3" fill="#0369a1">SW {d.weather.windSpeed}km/h</text>
            </g>

            {/* Station pins */}
            {d.stations.map(s => {
              const sm = aqiMeta(s.aqi);
              const isSelected = selected === s.id;
              const isOffline = s.status !== "Online";
              return (
                <g key={s.id} onClick={() => setSelected(s.id === selected ? null : s.id)} style={{ cursor: "pointer" }}>
                  {/* Pulse ring for high AQI */}
                  {s.aqi > 150 && !isOffline && (
                    <circle cx={s.x} cy={s.y} r={isSelected ? 6 : 5} fill="none" stroke={sm.color} strokeWidth="0.6" opacity="0.4" />
                  )}
                  {/* Pin circle */}
                  <circle cx={s.x} cy={s.y} r={isSelected ? 5 : 3.5}
                    fill={isOffline ? "#d1d5db" : sm.color}
                    stroke="white" strokeWidth={isSelected ? 1.2 : 0.8}
                    opacity={isOffline ? 0.5 : 1} />
                  {/* AQI value */}
                  <text x={s.x} y={s.y + 0.7} textAnchor="middle" fontSize={isSelected ? 2 : 1.6} fontWeight="700" fill="white">{s.aqi}</text>
                  {/* Station name */}
                  <text x={s.x} y={s.y + 6} textAnchor="middle" fontSize="1.5" fill="#374151" fontWeight="500">
                    {s.shortName}
                  </text>
                  {/* Offline indicator */}
                  {isOffline && (
                    <text x={s.x + 4} y={s.y - 3} fontSize="2" fill="#ef4444">⊘</text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Station detail */}
        {sel ? (
          <div className="aqm-station-detail">
            <div className="aqm-sd-header" style={{ borderColor: selM.color }}>
              <div>
                <div className="aqm-sd-type">{TYPE_ICONS[sel.type]} {sel.type}</div>
                <div className="aqm-sd-name">{sel.name}</div>
                <div className="aqm-sd-id">{sel.id} · {sel.status === "Online" ? "🟢 Online" : "🔴 Offline"} · {sel.lastSeen}</div>
              </div>
              <AqiBadge aqi={sel.aqi} />
            </div>

            <div className="aqm-sd-aqi-big">
              <div style={{ color: selM.color, fontSize: 44, fontWeight: 800, lineHeight: 1 }}>{sel.aqi}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>Air Quality Index</div>
            </div>

            <div className="aqm-sd-params">
              {[
                { label: "PM2.5",   value: `${sel.pm25} µg/m³`,  color: pm25Color(sel.pm25),  limit: "< 60" },
                { label: "PM10",    value: `${sel.pm10} µg/m³`,  color: pm25Color(sel.pm10/2), limit: "< 100" },
                { label: "SO₂",    value: `${sel.so2} µg/m³`,   color: sel.so2 > 60 ? "#ef4444" : "#10b981", limit: "< 80" },
                { label: "NO₂",    value: `${sel.no2} µg/m³`,   color: sel.no2 > 60 ? "#f59e0b" : "#10b981", limit: "< 80" },
                { label: "CO",      value: `${sel.co} mg/m³`,    color: sel.co > 1.5 ? "#f59e0b" : "#10b981", limit: "< 2" },
                { label: "O₃",     value: `${sel.o3} µg/m³`,    color: sel.o3 > 80 ? "#f59e0b" : "#10b981", limit: "< 100" },
                { label: "Temp",    value: `${sel.temperature}°C`, color: "#f97316" },
                { label: "Wind",    value: `${sel.windSpeed} km/h ${sel.windDir}`, color: "#3b82f6" },
              ].map(p => (
                <div key={p.label} className="aqm-sdp-card">
                  <div className="aqm-sdp-val" style={{ color: p.color }}>{p.value}</div>
                  <div className="aqm-sdp-lbl">{p.label}</div>
                  {p.limit && <div style={{ fontSize: 9, color: "#9ca3af" }}>Limit: {p.limit}</div>}
                </div>
              ))}
            </div>

            {sel.aqi > 150 && (
              <div className="aqm-sd-ai">
                🤖 AI: {sel.type === "Industrial" ? "Industrial emissions primary driver. Wind transporting pollutants SW→residential. Issue source notification." :
                  sel.type === "Traffic" ? "Vehicle emission spike. Suggest diverting heavy transport vehicles via alternate routes." :
                  "High pollution levels. Issue public health advisory for sensitive groups."}
              </div>
            )}

            <button className="aqm-ctrl-btn aqm-ctrl-blue" style={{ marginTop: 10, width: "100%" }} onClick={() => setSelected(null)}>✕ Close</button>
          </div>
        ) : (
          <div className="aqm-station-nosel">
            <div style={{ fontSize: 40 }}>🗺</div>
            <div className="aqm-fns-title">Select a monitoring station</div>
            <div className="aqm-fns-sub">Click any AQI pin on the map to view detailed pollutant data</div>
            <div style={{ marginTop: 12 }}>
              {d.stations.map(s => {
                const sm = aqiMeta(s.aqi);
                return (
                  <div key={s.id} className="aqm-fsg-chip" style={{ background: sm.bg, borderColor: sm.color, color: sm.text }}
                    onClick={() => setSelected(s.id)}>
                    {TYPE_ICONS[s.type]} {s.shortName}: AQI {s.aqi}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="aqm-ai-strip">
        🤖 AI: Dominant wind direction SW at 14.5 km/h is transporting industrial emissions from {d.stations[0].name} toward residential areas.
        AQI at residential station ({d.stations[1].aqi}) is 85% correlated with industrial zone levels. Rain forecast Sunday will provide natural washout.
      </div>
    </div>
  );
};

export default AqmStationMapPanel;
